import { Button, Id, Loading, Section, SubmitButton } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik, FormikErrors } from "formik";
import { Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";
import styled from "styled-components";
import { usePMAnalytics } from "../../analytics/PMAnalyticsProvider";
import { useRegion } from "../../region/RegionProvider";
import { registrationFormService } from "../../shared/api/registrationForms";
import { useConfig } from "../../shared/hooks/useConfig";
import { RegistrationShopSummaryResource, RegistrationSummaryNotAvailableResource } from "../../shared/types";
import { ErrorCard } from "../../shared/ui/ErrorCard";
import { ConsentFormFields } from "../consents/ConsentFormFields";
import { DataFieldValueRenderer } from "../DataFieldValueRenderer";
import { ConsentValues, useRegistrationForm } from "../provider/registrationFormProviderContext";
import {
	getSectionsFromStep,
	isSectionHidden,
	toConsentErrors,
	toConsentValues,
	toGuestAttributeChildrenFromSection,
} from "../registrationFormUtils";
import { RenderRegistrationBlock } from "../RenderRegistrationBlock";
import { HotelSummary } from "../section/hotel/HotelSummary";
import { TravelSummary } from "../section/travel/TravelSummary";
import { SummaryValue } from "../SummaryValue";
import { ValueLabel } from "../ValueLabel";
import { RegistrationCompanionSummary } from "./RegistrationCompanionSummary";
import { toPaymentProviderErrors } from "./shopSummary/PaymentProviderSelect";
import { RegistrationShopSummary } from "./shopSummary/RegistrationShopSummary";

const StyledSection = styled(Section)`
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
	padding: ${({ theme }) => theme.spacing(4)};
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 0 0 ${({ theme }) => theme.spacing(4)} 0;
	gap: ${({ theme }) => theme.spacing(4)};
`;

interface SummaryFormValues {
	consentValues: ConsentValues;
	paymentProviderId: Id | null;
}

const isSummarySuccess = (
	resource: RegistrationShopSummaryResource | RegistrationSummaryNotAvailableResource,
): resource is RegistrationShopSummaryResource => {
	return (resource as any).hasSummaryError === undefined;
};

export const RegistrationSummaryPage = () => {
	const { trackSummaryAborted, trackRegistrationCompleted, trackRegistrationFailed } = usePMAnalytics();
	const { isPreview } = useRegistrationForm();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { regionId } = useRegion();
	const {
		dataFieldSettings: { emailId },
	} = useConfig();
	const {
		errors,
		hiddenSteps,
		getHiddenDataFields,
		getHiddenSections,
		filledSteps,
		previousStep,
		submitRegistration,
		registrationForm,
		travels,
		isShopEnabled,
		submitRegistrationFormDataForShopSummary,
	} = useRegistrationForm();
	const emailDomain = useMemo(() => {
		if (emailId) {
			const values = Object.values(filledSteps).find((x) => x[emailId]);
			if (values) {
				const [, domain] = values[emailId].split("@");
				return `@${domain}`;
			}
		}

		return "";
	}, [filledSteps, emailId]);
	const { data: consents = [], isLoading } = useQuery({
		queryKey: [
			...registrationFormService.keys.consentsForSummaryPage(registrationForm.id),
			regionId ?? null,
			emailDomain,
		],
		queryFn: () =>
			registrationFormService
				.consentsForSummaryPage(registrationForm.id, regionId ?? null, emailDomain)
				.then((x) => x.data.data),
	});
	const { data: shopSummaryResource, isLoading: isShopLoading } = useQuery({
		enabled: isShopEnabled,
		queryKey: ["shop-summary"],
		queryFn: () =>
			submitRegistrationFormDataForShopSummary({
				summaryPageConsents: {},
				paymentProviderId: null,
				shopSummaryHash: null,
			}),
	});
	const hiddenSections = getHiddenSections(filledSteps);
	const hiddenDataFields = getHiddenDataFields(filledSteps);
	const isTravelSectionHidden = isSectionHidden(registrationForm, "travel", filledSteps);

	const onSubmit = useCallback(
		async ({ consentValues, paymentProviderId }: SummaryFormValues) => {
			const success = await submitRegistration({
				summaryPageConsents: consentValues,
				paymentProviderId,
				shopSummaryHash: (shopSummaryResource as RegistrationShopSummaryResource | undefined)?.shopSummaryHash ?? null,
			});

			if (success) {
				trackRegistrationCompleted(`${registrationForm.id}`);
			} else {
				trackRegistrationFailed(`${registrationForm.id}`);
			}
		},

		[submitRegistration, shopSummaryResource, trackRegistrationCompleted, registrationForm.id, trackRegistrationFailed],
	);

	const handleBack = useCallback(() => {
		const previousStepId = previousStep();
		if (previousStepId !== null) {
			trackSummaryAborted(`${registrationForm.id}`, previousStepId);
			navigate(`../${previousStepId}`);
		}
	}, [navigate, previousStep, registrationForm.id, trackSummaryAborted]);

	if (shopSummaryResource && !isSummarySuccess(shopSummaryResource)) {
		return (
			<StyledSection color="content">
				<ErrorCard>{t("The shop is currently not fully configured. Please contact an administrator.")}</ErrorCard>
			</StyledSection>
		);
	}

	if (!Object.keys(filledSteps).length) {
		return <Navigate to=".." />;
	}

	if (isLoading || (isShopEnabled && isShopLoading && !shopSummaryResource))
		return (
			<StyledSection color="content">
				<Loading />
			</StyledSection>
		);

	const hasShopItems = Boolean(
		(shopSummaryResource?.transactionItemsToBePaidNow.length ?? 0) +
		(shopSummaryResource?.transactionItemsAlreadyProcessed.length ?? 0) +
		(shopSummaryResource?.transactionItemsToBeRefunded.length ?? 0),
	);

	return (
		<>
			{registrationForm.summaryBlockId && <RenderRegistrationBlock blockId={registrationForm.summaryBlockId} />}
			<StyledSection color="content">
				{registrationForm.steps
					.filter((x) => !hiddenSteps.includes(x.id))
					.map((step) => {
						const filledStep = filledSteps[step.id] ?? {};
						const validSections = getSectionsFromStep(step).filter((x) => !hiddenSections.includes(x.section.id));

						return (
							<Fragment key={step.id}>
								{validSections.map(({ section }) => {
									if (section.type === "companion") {
										return <RegistrationCompanionSummary key={section.id} section={section} />;
									}
									if (section.type === "hotel" && filledStep.hotelSections?.[section.hotelSettings.hotelStayTypeId]) {
										return (
											<HotelSummary
												key={section.id}
												guestId={registrationForm.guest.id === "new" ? null : registrationForm.guest.id}
												sectionId={section.id}
												formValues={filledStep.hotelSections[section.hotelSettings.hotelStayTypeId]}
											/>
										);
									}

									const dataFields = toGuestAttributeChildrenFromSection(section)
										.filter((x) => !hiddenDataFields.includes(`${section.id}-${x.dataField.id}`))
										.map((x) => x.dataField);

									return (
										<Fragment key={section.id}>
											{dataFields.map((dataField, index) => (
												<SummaryValue key={dataField.id}>
													<ValueLabel>{dataField.label}:</ValueLabel>
													<DataFieldValueRenderer
														dataField={dataField}
														value={filledStep[dataField.id]}
														error={errors[`dataFieldValues.${index}.value`]}
													/>
												</SummaryValue>
											))}
										</Fragment>
									);
								})}
							</Fragment>
						);
					})}
			</StyledSection>
			{!isTravelSectionHidden && (
				<StyledSection color="content">
					<TravelSummary travels={travels} />
				</StyledSection>
			)}

			<StyledSection color="content">
				<Formik
					initialValues={{ consentValues: toConsentValues(consents), paymentProviderId: null }}
					onSubmit={onSubmit}
					validate={(values: SummaryFormValues) => {
						const errors: FormikErrors<SummaryFormValues> = {};
						const consentErrors = toConsentErrors(consents, values.consentValues, t);

						const paymentProviderErrors = shopSummaryResource
							? toPaymentProviderErrors(shopSummaryResource, values.paymentProviderId, t)
							: {};

						if (Object.keys(consentErrors).length) {
							errors.consentValues = consentErrors;
						}

						return { ...errors, ...paymentProviderErrors };
					}}
				>
					<Form>
						{isShopEnabled && shopSummaryResource && hasShopItems && (
							<RegistrationShopSummary shopSummaryResource={shopSummaryResource} />
						)}
						{consents.length > 0 && <ConsentFormFields name="consentValues" consents={consents} />}
						<ButtonWrapper>
							<Button type="button" onClick={handleBack}>
								{t("Back")}
							</Button>
							<SubmitButton
								color="primary"
								disabled={isPreview || (isShopEnabled && shopSummaryResource === undefined) || !regionId}
							>
								{t(
									isShopEnabled && (shopSummaryResource?.transactionItemsToBePaidNow.length ?? 0) > 0
										? "Submit and pay"
										: "registration.submit",
								)}
							</SubmitButton>
						</ButtonWrapper>
					</Form>
				</Formik>
			</StyledSection>
		</>
	);
};
